'use client'

import { useEffect, useMemo, useState } from 'react'
import { Address, zeroAddress } from 'viem'
import { useAccount, useBalance } from 'wagmi'

import { NATIVE_HYPE_GAS_RESERVE } from '@/config/consts'
import { E_LEND_CHAIN_ID, isStablecoin } from '@/config/euler-contracts'
import { isWhypeAddress } from '@/domain/euler/nativeHype'
import { useAvailableSubAccount } from '@/domain/euler/positions/useAvailableSubAccount'
import { useUserCollateralState } from '@/domain/euler/rebalance'
import { useWalletTokenBalances } from '@/domain/euler/wallet/useWalletTokenBalances'
import { useBorrowDetails } from '@/features/eborrow-details/logic/useBorrowDetails'
import { useBorrowPairs } from '@/features/eborrow/logic/useBorrowPairs'
import { topMarkets } from '@/features/markets/logic/special-entries'
import { Skeleton } from '@/ui/atoms/skeleton/Skeleton'
import { Typography } from '@/ui/atoms/typography/Typography'

import {
  BorrowAssetOption,
  MarketExplorer,
  RouteDirection,
  RouteInfo,
  RoutePositions,
} from './components/MarketExplorer'
import { PooledBorrowPanel } from './components/PooledBorrowPanel'
import { PositionComposer } from './components/PositionComposer'
import { usePooledRoutes } from './hooks/usePooledRoutes'

function getRouteDirection(params: { collateralSymbol?: string; liabilitySymbol?: string }): RouteDirection {
  const { collateralSymbol, liabilitySymbol } = params
  if (!collateralSymbol || !liabilitySymbol) return 'neutral'

  const borrowIsStable = isStablecoin(liabilitySymbol)
  const collateralIsStable = isStablecoin(collateralSymbol)

  if (borrowIsStable && !collateralIsStable) return 'long'
  if (!borrowIsStable && collateralIsStable) return 'short'
  return 'neutral'
}

const DEFAULT_COLLATERAL_SYMBOL = 'WHYPE'
const DEFAULT_BORROW_UNDERLYING = '0xB8CE59FC3717ada4C02eaDF9682A9e934F625ebb'
const TOP_MARKET_ORDER = topMarkets.map((symbol) => symbol.replace(/[^a-z0-9]/gi, '').toUpperCase())

function normalizeSymbol(symbol: string): string {
  return symbol.replace(/[^a-z0-9]/gi, '').toUpperCase()
}

function getSymbolPriority(symbol: string): number {
  const normalized = normalizeSymbol(symbol)
  const topIndex = TOP_MARKET_ORDER.indexOf(normalized === 'WHYPE' ? 'HYPE' : normalized)
  if (topIndex !== -1) return topIndex
  if (isStablecoin(symbol) || normalized === 'USDT0') return TOP_MARKET_ORDER.length + 1
  return TOP_MARKET_ORDER.length + 2
}

// Reuse the scoring logic
function scoreRoute(params: { maxLtvBps?: bigint; borrowApy?: number; liquidityUsd?: number }): number {
  const ltv = params.maxLtvBps ? Number(params.maxLtvBps) : 0
  const apy = params.borrowApy ?? 0
  const liq = params.liquidityUsd ?? 0

  const ltvScore = ltv / 10000
  const apyScore = 1 / (1 + Math.max(0, apy) / 10)
  const liqScore = Math.log10(1 + Math.max(0, liq)) / 10

  return ltvScore * 0.45 + apyScore * 0.35 + liqScore * 0.2
}

function scoreRouteWithPreference(params: {
  maxLtvBps?: bigint
  borrowApy?: number
  liquidityUsd?: number
  collateralSymbol?: string
  liabilitySymbol?: string
}): number {
  const base = scoreRoute(params)
  const direction = getRouteDirection(params)
  if (direction === 'long') return base + 0.1
  if (direction === 'short') return base - 0.05
  return base
}

function findBestRouteForAmount<
  T extends {
    maxLTV?: bigint
    borrowAPY?: number
    liquidityUSD?: number
    collateralSymbol?: string
    liabilitySymbol?: string
    collateral: Address
    liability: Address
  },
>(routes: T[], borrowAmountUsd: number | undefined): T | undefined {
  if (routes.length === 0) return undefined
  const firstRoute = routes[0]
  if (!firstRoute) return undefined

  if (!borrowAmountUsd || borrowAmountUsd <= 0) {
    return firstRoute
  }

  const requiredLiquidity = borrowAmountUsd * 1.05
  const viableRoutes = routes.filter((r) => (r.liquidityUSD ?? 0) >= requiredLiquidity)

  if (viableRoutes.length === 0) {
    return routes.reduce((best, r) => ((r.liquidityUSD ?? 0) > (best.liquidityUSD ?? 0) ? r : best), firstRoute)
  }

  return viableRoutes[0]
}

export function EulerQuickBorrowContainer() {
  const { address } = useAccount()
  const { pairs, isLoading } = useBorrowPairs(address as Address | undefined)

  // Fetch pooled market routes
  const {
    routes: pooledRoutes,
    userPooledPosition,
    collateralOptions: pooledCollateralOptions,
    borrowOptions: pooledBorrowOptions,
  } = usePooledRoutes()

  // Fetch wallet token balances for collateral options
  const { tokens: walletTokens } = useWalletTokenBalances(E_LEND_CHAIN_ID)

  // Create a map of token address -> { balance, priceUsd }
  const walletTokenMap = useMemo(() => {
    const map = new Map<string, { balance: string; priceUsd: number }>()
    for (const wt of walletTokens) {
      map.set(wt.token.address.toLowerCase(), {
        balance: wt.balance.toString(),
        priceUsd: Number(wt.token.unitPriceUsd),
      })
    }
    return map
  }, [walletTokens])

  const [selectedBorrowUnderlying, setSelectedBorrowUnderlying] = useState<Address | undefined>(undefined)
  const [selectedCollateralUnderlying, setSelectedCollateralUnderlying] = useState<Address | undefined>(undefined)
  const [selectedRouteKey, setSelectedRouteKey] = useState<string | undefined>(undefined)
  // Position selection: subAccountId number for existing, 'new' for create new, undefined for no selection
  const [selectedPositionId, setSelectedPositionId] = useState<number | 'new' | undefined>(undefined)
  // Amount inputs
  const [collateralAmount, setCollateralAmount] = useState<string>('')
  const [borrowAmount, setBorrowAmount] = useState<string>('')
  const [routeAutoAdjusted, setRouteAutoAdjusted] = useState<boolean>(false)
  // Native HYPE selection (for WHYPE-based vaults)
  const [useNativeCollateral, setUseNativeCollateral] = useState<boolean>(true) // Default to native HYPE
  const [receiveNativeBorrow, setReceiveNativeBorrow] = useState<boolean>(true) // Default to native HYPE
  // Active strategy tab (Borrow vs Multiply)
  const [activeStrategy, setActiveStrategy] = useState<'Borrow' | 'Multiply'>('Borrow')

  // -- Data Processing (Same as before, just kept for logic) --

  const allCollateralOptions = useMemo(() => {
    const map = new Map<string, BorrowAssetOption & { liquidityUsdHint: number }>()

    // Add Euler collateral options
    for (const p of pairs) {
      if (!p.collateralUnderlying || !p.collateralSymbol) continue
      const key = p.collateralUnderlying.toLowerCase()
      const existing = map.get(key)
      const liq = p.liquidityUSD ?? 0

      if (!existing || liq > existing.liquidityUsdHint) {
        // Get balance and price from wallet token map
        const walletData = walletTokenMap.get(key)
        map.set(key, {
          underlying: p.collateralUnderlying,
          symbol: p.collateralSymbol,
          name: p.collateralTokenInfo?.name,
          logoURI: p.collateralTokenInfo?.logoURI,
          tokenInfo: p.collateralTokenInfo,
          liquidityUsdHint: liq,
          balance: walletData?.balance,
          priceUsd: walletData?.priceUsd,
        })
      }
    }

    // Add pooled collateral options (merge by address, don't overwrite Euler if exists)
    for (const opt of pooledCollateralOptions) {
      const key = opt.underlying.toLowerCase()
      if (!map.has(key)) {
        const walletData = walletTokenMap.get(key)
        map.set(key, {
          underlying: opt.underlying,
          symbol: opt.symbol,
          name: opt.name,
          logoURI: opt.logoURI,
          tokenInfo: undefined,
          liquidityUsdHint: 0,
          balance: walletData?.balance,
          priceUsd: walletData?.priceUsd,
        })
      }
    }

    return Array.from(map.values()).sort((a, b) => {
      const priorityDiff = getSymbolPriority(a.symbol) - getSymbolPriority(b.symbol)
      if (priorityDiff !== 0) return priorityDiff
      const liquidityDiff = (b.liquidityUsdHint ?? 0) - (a.liquidityUsdHint ?? 0)
      if (liquidityDiff !== 0) return liquidityDiff
      return a.symbol.localeCompare(b.symbol)
    })
  }, [pairs, walletTokenMap, pooledCollateralOptions])

  const borrowOptionsForCollateral = useMemo(() => {
    if (!selectedCollateralUnderlying) return []
    const map = new Map<string, BorrowAssetOption & { liquidityUsdHint?: number }>()

    // Add Euler borrow options for selected collateral
    for (const p of pairs) {
      if (!p.liabilityUnderlying || !p.liabilitySymbol || !p.collateralUnderlying) continue
      if (p.collateralUnderlying.toLowerCase() !== selectedCollateralUnderlying.toLowerCase()) continue
      const key = p.liabilityUnderlying.toLowerCase()
      const existing = map.get(key)
      const liq = p.liquidityUSD ?? 0

      if (!existing || liq > (existing.liquidityUsdHint ?? 0)) {
        map.set(key, {
          underlying: p.liabilityUnderlying,
          symbol: p.liabilitySymbol,
          name: p.liabilityTokenInfo?.name,
          logoURI: p.liabilityTokenInfo?.logoURI,
          tokenInfo: p.liabilityTokenInfo,
          liquidityUsdHint: liq,
        })
      }
    }

    // Check if selected collateral exists in pooled markets
    const collateralInPooled = pooledCollateralOptions.some(
      (opt) => opt.underlying.toLowerCase() === selectedCollateralUnderlying.toLowerCase(),
    )

    // Add pooled borrow options if collateral is available in pooled markets
    if (collateralInPooled) {
      for (const opt of pooledBorrowOptions) {
        const key = opt.underlying.toLowerCase()
        if (!map.has(key)) {
          map.set(key, {
            underlying: opt.underlying,
            symbol: opt.symbol,
            name: opt.name,
            logoURI: opt.logoURI,
            tokenInfo: undefined,
            liquidityUsdHint: 0,
          })
        }
      }
    }

    return Array.from(map.values()).sort((a, b) => {
      const priorityDiff = getSymbolPriority(a.symbol) - getSymbolPriority(b.symbol)
      if (priorityDiff !== 0) return priorityDiff
      const liquidityDiff = (b.liquidityUsdHint ?? 0) - (a.liquidityUsdHint ?? 0)
      if (liquidityDiff !== 0) return liquidityDiff
      return a.symbol.localeCompare(b.symbol)
    })
  }, [pairs, selectedCollateralUnderlying, pooledCollateralOptions, pooledBorrowOptions])

  useEffect(() => {
    if (selectedCollateralUnderlying) return
    const defaultCollateral = allCollateralOptions.find((option) => option.symbol === DEFAULT_COLLATERAL_SYMBOL)
    if (defaultCollateral) {
      setSelectedCollateralUnderlying(defaultCollateral.underlying)
    }
  }, [allCollateralOptions, selectedCollateralUnderlying])

  useEffect(() => {
    if (borrowOptionsForCollateral.length === 0) return

    const selectedStillValid = selectedBorrowUnderlying
      ? borrowOptionsForCollateral.some(
          (option) => option.underlying.toLowerCase() === selectedBorrowUnderlying.toLowerCase(),
        )
      : false

    if (selectedStillValid) return

    const defaultBorrowByAddress = borrowOptionsForCollateral.find(
      (option) => option.underlying.toLowerCase() === DEFAULT_BORROW_UNDERLYING.toLowerCase(),
    )
    const defaultBorrowBySymbol = borrowOptionsForCollateral.find(
      (option) => normalizeSymbol(option.symbol) === 'USDT0',
    )

    const nextBorrow =
      defaultBorrowByAddress?.underlying ??
      defaultBorrowBySymbol?.underlying ??
      borrowOptionsForCollateral[0]?.underlying

    if (nextBorrow) {
      setSelectedBorrowUnderlying(nextBorrow)
    }
  }, [borrowOptionsForCollateral, selectedBorrowUnderlying])

  const candidateRoutes = useMemo(() => {
    if (!selectedBorrowUnderlying || !selectedCollateralUnderlying) return []

    const filtered = pairs.filter((p) => {
      if (!p.collateralUnderlying || !p.liabilityUnderlying) return false
      return (
        p.collateralUnderlying.toLowerCase() === selectedCollateralUnderlying.toLowerCase() &&
        p.liabilityUnderlying.toLowerCase() === selectedBorrowUnderlying.toLowerCase()
      )
    })

    // Sort by best score by default
    return filtered.sort((a, b) => {
      return (
        scoreRouteWithPreference({
          maxLtvBps: b.maxLTV,
          borrowApy: b.borrowAPY,
          liquidityUsd: b.liquidityUSD,
          collateralSymbol: b.collateralSymbol,
          liabilitySymbol: b.liabilitySymbol,
        }) -
        scoreRouteWithPreference({
          maxLtvBps: a.maxLTV,
          borrowApy: a.borrowAPY,
          liquidityUsd: a.liquidityUSD,
          collateralSymbol: a.collateralSymbol,
          liabilitySymbol: a.liabilitySymbol,
        })
      )
    })
  }, [pairs, selectedBorrowUnderlying, selectedCollateralUnderlying])

  const selectedRoute = useMemo(() => {
    if (!selectedRouteKey) return undefined
    return candidateRoutes.find((p) => `${p.collateral}-${p.liability}` === selectedRouteKey)
  }, [candidateRoutes, selectedRouteKey])

  // Check if collateral/borrow underlying is WHYPE (supports native HYPE)
  const collateralIsWhype = useMemo(
    () => (selectedCollateralUnderlying ? isWhypeAddress(selectedCollateralUnderlying, E_LEND_CHAIN_ID) : false),
    [selectedCollateralUnderlying],
  )
  const borrowIsWhype = useMemo(
    () => (selectedBorrowUnderlying ? isWhypeAddress(selectedBorrowUnderlying, E_LEND_CHAIN_ID) : false),
    [selectedBorrowUnderlying],
  )

  // Fetch user's wallet balance for selected collateral token (WHYPE/ERC20)
  const { data: wrappedCollateralBalanceData } = useBalance({
    address,
    token: selectedCollateralUnderlying,
    chainId: E_LEND_CHAIN_ID,
    query: {
      enabled: !!address && !!selectedCollateralUnderlying,
    },
  })

  // Fetch native HYPE balance
  const { data: nativeHypeBalanceData } = useBalance({
    address,
    chainId: E_LEND_CHAIN_ID,
    query: {
      enabled: !!address && collateralIsWhype,
    },
  })

  // Calculate native HYPE balance with gas reserve
  const nativeHypeBalance = useMemo(() => {
    if (!nativeHypeBalanceData) return '0'
    const balance = Number(nativeHypeBalanceData.formatted)
    return Math.max(0, balance - NATIVE_HYPE_GAS_RESERVE).toString()
  }, [nativeHypeBalanceData])

  // Select appropriate collateral balance based on native/wrapped selection
  const collateralBalance = useMemo(() => {
    if (collateralIsWhype && useNativeCollateral) {
      return nativeHypeBalance
    }
    return wrappedCollateralBalanceData?.formatted
  }, [collateralIsWhype, useNativeCollateral, nativeHypeBalance, wrappedCollateralBalanceData])

  // Fetch prices and market data using useBorrowDetails when route is selected
  // Pass selectedPositionId so Account Lens fetches data for the correct sub-account
  const { data: borrowDetailsData } = useBorrowDetails({
    collateral: selectedRoute?.collateral ?? zeroAddress,
    liability: selectedRoute?.liability ?? zeroAddress,
    subAccountId: selectedPositionId !== 'new' ? selectedPositionId : null,
  })

  const { availableSubAccountId: newPositionSubAccountId, isLoading: isLoadingNewSubAccount } = useAvailableSubAccount({
    liabilityVault: selectedRoute?.liability,
    userAddress: address,
    chainId: E_LEND_CHAIN_ID,
    enabled: !!selectedRoute && selectedPositionId === 'new',
  })

  // Extract prices from borrow details
  const collateralPriceUsd = borrowDetailsData?.oraclePrice ?? undefined
  const borrowPriceUsd = borrowDetailsData?.liabilityStats?.priceUSD ?? undefined

  // Calculate max borrow based on collateral amount and max LTV
  const maxBorrowAmount = useMemo(() => {
    if (!collateralAmount || !collateralPriceUsd || !borrowPriceUsd || !selectedRoute?.maxLTV) {
      return undefined
    }
    const collateralValueUsd = Number(collateralAmount) * collateralPriceUsd
    const maxLtvPercent = Number(selectedRoute.maxLTV) / 100 // maxLTV is in basis points
    const maxBorrowUsd = (collateralValueUsd * maxLtvPercent) / 100
    const maxBorrow = maxBorrowUsd / borrowPriceUsd
    return maxBorrow > 0 ? maxBorrow.toString() : undefined
  }, [collateralAmount, collateralPriceUsd, borrowPriceUsd, selectedRoute?.maxLTV])

  // Get all unique vault addresses for position fetching
  const allVaultAddresses = useMemo(() => {
    const vaults = new Set<Address>()
    for (const p of pairs) {
      if (p.collateral) vaults.add(p.collateral)
      if (p.liability) vaults.add(p.liability)
    }
    return Array.from(vaults)
  }, [pairs])

  // Build vault logo map for SubAccountPositions
  const vaultLogos = useMemo(() => {
    const map = new Map<string, string | undefined>()
    for (const p of pairs) {
      if (p.collateral && p.collateralTokenInfo?.logoURI) {
        map.set(p.collateral.toLowerCase(), p.collateralTokenInfo.logoURI)
      }
      if (p.liability && p.liabilityTokenInfo?.logoURI) {
        map.set(p.liability.toLowerCase(), p.liabilityTokenInfo.logoURI)
      }
    }
    return map
  }, [pairs])

  // Fetch user positions for all vaults
  const { state: positionState } = useUserCollateralState({
    userAddress: address,
    vaultAddresses: allVaultAddresses,
    chainId: E_LEND_CHAIN_ID,
    enabled: !!address && allVaultAddresses.length > 0,
  })

  // Build a map of route key -> positions that match that route
  // Group by subAccountId first, then filter by route
  const routePositionsMap = useMemo(() => {
    const map = new Map<string, RoutePositions[]>()
    if (!positionState) return map

    // Get all active subaccounts
    const activeSubAccounts = new Set<number>()
    for (const c of positionState.collaterals) activeSubAccounts.add(c.subAccountId)
    for (const b of positionState.borrows) activeSubAccounts.add(b.subAccountId)

    for (const route of candidateRoutes) {
      const key = `${route.collateral}-${route.liability}`
      const collateralVaultLower = route.collateral?.toLowerCase()
      const liabilityVaultLower = route.liability?.toLowerCase()
      const matchingPositions: RoutePositions[] = []

      for (const subAccountId of activeSubAccounts) {
        const collaterals = positionState.collaterals.filter((c) => c.subAccountId === subAccountId)
        const borrows = positionState.borrows.filter((b) => b.subAccountId === subAccountId)

        // Check if this position matches the route:
        // 1. Has the route's collateral vault as supply with assets > 0
        const hasMatchingCollateral = collaterals.some(
          (c) => c.vaultAddress.toLowerCase() === collateralVaultLower && c.assets > 0n,
        )
        if (!hasMatchingCollateral) continue

        // 2. Check controller compatibility
        const controller = positionState.controllers.get(subAccountId)
        const controllerLower = controller?.toLowerCase()
        const controllerMatchesRoute =
          !controller || // No controller = can use for any route
          controllerLower === liabilityVaultLower

        // 3. Check if controller can be switched (different controller but no debt)
        // Calculate total debt for this subaccount
        const totalDebt = borrows.reduce((sum, b) => sum + b.debt, 0n)
        const hasDebt = totalDebt > 0n
        const canSwitchController = !controllerMatchesRoute && controller && !hasDebt

        // Skip if controller doesn't match AND we can't switch (has debt)
        if (!controllerMatchesRoute && !canSwitchController) continue

        // 4. If has debt, borrow must match route's liability vault
        if (hasDebt) {
          const borrowMatchesRoute = borrows.some((b) => b.vaultAddress.toLowerCase() === liabilityVaultLower)
          if (!borrowMatchesRoute) continue
        }

        matchingPositions.push({
          subAccountId,
          collaterals,
          borrows,
          // Mark if controller needs to be switched
          needsControllerSwitch: canSwitchController,
          existingController: canSwitchController ? controller : undefined,
        })
      }

      if (matchingPositions.length > 0) {
        map.set(key, matchingPositions)
      }
    }
    return map
  }, [positionState, candidateRoutes])

  // Build a set of liability vaults where user has existing borrow positions
  // This is used to determine if "Create new position" should be shown
  const liabilitiesWithExistingPositions = useMemo(() => {
    const set = new Set<string>()
    if (!positionState) return set

    for (const borrow of positionState.borrows) {
      if (borrow.debt > 0n) {
        set.add(borrow.vaultAddress.toLowerCase())
      }
    }
    return set
  }, [positionState])

  // Calculate borrow amount in USD for route selection
  const borrowAmountUsd = useMemo(() => {
    if (!borrowAmount || !borrowPriceUsd) return undefined
    const amount = Number(borrowAmount)
    if (Number.isNaN(amount) || amount <= 0) return undefined
    return amount * borrowPriceUsd
  }, [borrowAmount, borrowPriceUsd])

  // Auto-select best route if available, considering borrow amount for liquidity
  useEffect(() => {
    if (candidateRoutes.length === 0) return

    // Find best route considering borrow amount (liquidity adequacy)
    const bestRouteForAmount = findBestRouteForAmount(candidateRoutes, borrowAmountUsd)
    if (!bestRouteForAmount) return

    const bestKey = `${bestRouteForAmount.collateral}-${bestRouteForAmount.liability}`

    // Check if this differs from the "best by score" route (first in sorted list)
    const bestByScore = candidateRoutes[0]
    const bestByScoreKey = bestByScore ? `${bestByScore.collateral}-${bestByScore.liability}` : undefined
    const wasAdjusted = borrowAmountUsd !== undefined && borrowAmountUsd > 0 && bestKey !== bestByScoreKey

    setSelectedRouteKey((prev) => {
      // If user hasn't selected a route, auto-select the best one
      if (!prev) {
        setRouteAutoAdjusted(wasAdjusted)
        return bestKey
      }

      // If user has a route selected but borrow amount changed and requires a different route
      // Only auto-switch if the current selection doesn't have enough liquidity
      if (borrowAmountUsd && borrowAmountUsd > 0) {
        const currentRoute = candidateRoutes.find((r) => `${r.collateral}-${r.liability}` === prev)
        const currentLiquidity = currentRoute?.liquidityUSD ?? 0
        const requiredLiquidity = borrowAmountUsd * 1.05

        if (currentLiquidity < requiredLiquidity && bestRouteForAmount.liquidityUSD !== undefined) {
          // Current route doesn't have enough liquidity, switch to better one
          setRouteAutoAdjusted(true)
          return bestKey
        }
      }

      // Keep current selection
      return prev
    })
  }, [candidateRoutes, borrowAmountUsd])

  // Auto-select position when positions become available for the selected route
  useEffect(() => {
    if (!selectedRouteKey) return
    // Only auto-select if no position is currently selected
    if (selectedPositionId !== undefined) return

    const positions = routePositionsMap.get(selectedRouteKey)
    const firstPosition = positions?.[0]
    if (firstPosition) {
      setSelectedPositionId(firstPosition.subAccountId)
    } else {
      // Check if we can create a new position (user has existing position with this liability)
      const [, liability] = selectedRouteKey.split('-')
      if (liability && liabilitiesWithExistingPositions.has(liability.toLowerCase())) {
        setSelectedPositionId('new')
      }
    }
  }, [selectedRouteKey, selectedPositionId, routePositionsMap, liabilitiesWithExistingPositions])

  // Map to RouteInfo - Euler routes (must be before any early returns to follow hooks rules)
  const eulerRouteInfos: RouteInfo[] = useMemo(
    () =>
      candidateRoutes.map((r) => ({
        collateral: r.collateral,
        liability: r.liability,
        collateralSymbol: r.collateralSymbol,
        liabilitySymbol: r.liabilitySymbol,
        collateralTokenInfo: r.collateralTokenInfo,
        liabilityTokenInfo: r.liabilityTokenInfo,
        borrowAPY: r.borrowAPY,
        maxLTV: r.maxLTV,
        liquidityUSD: r.liquidityUSD,
        direction: getRouteDirection({ collateralSymbol: r.collateralSymbol, liabilitySymbol: r.liabilitySymbol }),
        hasPosition: r.hasPosition ?? false,
        clusterName: r.collateralMarket?.name ?? r.liabilityMarket?.name,
        routeType: 'euler' as const,
        userCollateralDeposit: r.userCollateralDeposit,
        userCollateralDepositUsd: r.userCollateralDepositUsd,
        userBorrowAmount: r.userBorrowAmount,
        userBorrowAmountUsd: r.userBorrowAmountUsd,
        collateralSupplyAPY: r.collateralSupplyAPY,
        liabilitySupplyAPY: r.liabilitySupplyAPY,
        collateralDecimals: r.collateralDecimals,
        liabilityDecimals: r.liabilityDecimals,
      })),
    [candidateRoutes],
  )

  // Filter pooled routes based on selected collateral and borrow tokens
  const filteredPooledRoutes = useMemo(() => {
    if (pooledRoutes.length === 0) return pooledRoutes

    return pooledRoutes.filter((route) => {
      // If collateral is selected, filter by it
      if (selectedCollateralUnderlying) {
        if (route.collateral.toLowerCase() !== selectedCollateralUnderlying.toLowerCase()) {
          return false
        }
      }
      // If borrow is selected, filter by it
      if (selectedBorrowUnderlying) {
        if (route.liability.toLowerCase() !== selectedBorrowUnderlying.toLowerCase()) {
          return false
        }
      }
      return true
    })
  }, [pooledRoutes, selectedCollateralUnderlying, selectedBorrowUnderlying])

  // Combine all routes (Euler + Pooled)
  const routeInfos = useMemo(() => {
    // Only combine if we have filtered pooled routes to add
    if (filteredPooledRoutes.length === 0) return eulerRouteInfos
    return [...eulerRouteInfos, ...filteredPooledRoutes]
  }, [eulerRouteInfos, filteredPooledRoutes])

  // Get selected route info with routeType
  const selectedRouteInfo = useMemo(() => {
    if (!selectedRouteKey) return undefined
    return routeInfos.find((r) => `${r.collateral}-${r.liability}` === selectedRouteKey)
  }, [routeInfos, selectedRouteKey])

  // --- Rendering ---

  // Show loading while Euler data is loading
  if (isLoading) {
    return (
      <div className="flex flex-col gap-6">
        <div className="rounded-3xl border border-white/10 bg-white/[.02] p-6">
          <Skeleton className="h-40 w-full rounded-xl" />
        </div>
        <div className="rounded-3xl border border-white/10 bg-white/[.02] p-6">
          <Skeleton className="h-80 w-full rounded-xl" />
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4 lg:flex-row">
      <div className="lg:basis-2/3">
        <MarketExplorer
          collateralOptions={allCollateralOptions}
          selectedCollateralUnderlying={selectedCollateralUnderlying}
          onSelectCollateral={(addr) => {
            setSelectedCollateralUnderlying(addr)
            setSelectedRouteKey(undefined)
            setSelectedPositionId(undefined)
            setCollateralAmount('')
            setBorrowAmount('')
            setUseNativeCollateral(true)
            setRouteAutoAdjusted(false)
          }}
          borrowOptions={borrowOptionsForCollateral}
          selectedBorrowUnderlying={selectedBorrowUnderlying}
          onSelectBorrow={(addr) => {
            setSelectedBorrowUnderlying(addr)
            setSelectedRouteKey(undefined)
            setSelectedPositionId(undefined)
            setBorrowAmount('')
            setReceiveNativeBorrow(true)
            setRouteAutoAdjusted(false)
          }}
          routes={routeInfos}
          selectedRouteKey={selectedRouteKey}
          onSelectRoute={(key) => {
            setSelectedRouteKey(key)
            setRouteAutoAdjusted(false)
          }}
          vaultLogos={vaultLogos}
          routePositionsMap={routePositionsMap}
          liabilitiesWithExistingPositions={liabilitiesWithExistingPositions}
          selectedPositionId={selectedPositionId}
          onSelectPosition={setSelectedPositionId}
          collateralAmount={collateralAmount}
          onCollateralAmountChange={setCollateralAmount}
          collateralBalance={collateralBalance}
          collateralPriceUsd={collateralPriceUsd}
          borrowAmount={borrowAmount}
          onBorrowAmountChange={setBorrowAmount}
          maxBorrowAmount={maxBorrowAmount}
          borrowPriceUsd={borrowPriceUsd}
          // Native HYPE support
          collateralSupportsNative={collateralIsWhype}
          useNativeCollateral={useNativeCollateral}
          onUseNativeCollateralChange={setUseNativeCollateral}
          nativeCollateralBalance={nativeHypeBalance}
          wrappedCollateralBalance={wrappedCollateralBalanceData?.formatted}
          borrowSupportsNative={borrowIsWhype}
          receiveNativeBorrow={receiveNativeBorrow}
          onReceiveNativeBorrowChange={setReceiveNativeBorrow}
          disableBorrowInput={activeStrategy === 'Multiply'}
          userPooledPosition={userPooledPosition}
          routeAutoAdjusted={routeAutoAdjusted}
        />
      </div>

      <div className="lg:basis-1/3">
        {selectedRouteInfo ? (
          (() => {
            // Check if this is a pooled route
            if (selectedRouteInfo.routeType === 'pooled') {
              return (
                <PooledBorrowPanel
                  collateral={selectedRouteInfo.collateral}
                  liability={selectedRouteInfo.liability}
                  collateralSymbol={selectedRouteInfo.collateralSymbol}
                  liabilitySymbol={selectedRouteInfo.liabilitySymbol}
                  borrowAPY={selectedRouteInfo.borrowAPY}
                  maxLTV={selectedRouteInfo.maxLTV}
                  liquidationLTV={selectedRouteInfo.liquidationLTV}
                  collateralAmount={collateralAmount}
                  borrowAmount={borrowAmount}
                  onBorrowAmountChange={setBorrowAmount}
                  collateralPriceUsd={selectedRouteInfo.collateralPriceUsd}
                  borrowPriceUsd={selectedRouteInfo.borrowPriceUsd}
                  useNativeCollateral={collateralIsWhype ? useNativeCollateral : false}
                />
              )
            }

            // Euler route - need selectedRoute for position data (has more detailed info)
            if (!selectedRoute || (selectedPositionId === 'new' && isLoadingNewSubAccount)) {
              return (
                <div className="flex items-center justify-center rounded-lg border border-border border-dashed bg-panel p-8 text-center">
                  <Typography variant="p" className="text-muted-foreground">
                    Loading route details...
                  </Typography>
                </div>
              )
            }

            // Get existing position values if modifying an existing position
            // Prefer Account Lens values (account-wide) for accurate multi-collateral LTV
            let existingCollateralUsd = 0
            let existingDebtUsd = 0
            let existingController: Address | undefined

            if (selectedPositionId !== 'new' && selectedPositionId !== undefined && selectedRouteKey) {
              const positions = routePositionsMap.get(selectedRouteKey)
              const selectedPosition = positions?.find((p) => p.subAccountId === selectedPositionId)
              if (selectedPosition) {
                // Use Account Lens data when available (accurate account-wide values)
                // This correctly handles multi-collateral positions with weighted LTVs
                if (borrowDetailsData?.userPosition?.accountTotalCollateralUSD !== undefined) {
                  existingCollateralUsd = borrowDetailsData.userPosition.accountTotalCollateralUSD
                  existingDebtUsd = borrowDetailsData.userPosition.accountTotalDebtUSD ?? 0
                } else {
                  // Fallback: only count collateral matching this route's collateral vault
                  // This is conservative but ensures pair-specific maxLTV is accurate
                  // (mixing collaterals with different max LTVs would overestimate capacity)
                  const routeCollateralLower = selectedRoute.collateral.toLowerCase()
                  existingCollateralUsd = selectedPosition.collaterals
                    .filter((c) => c.vaultAddress.toLowerCase() === routeCollateralLower)
                    .reduce((sum, c) => sum + c.valueUsd, 0)
                  existingDebtUsd = selectedPosition.borrows.reduce((sum, b) => sum + b.debtUsd, 0)
                }
                // Pass existing controller if it needs to be switched
                if (selectedPosition.needsControllerSwitch) {
                  existingController = selectedPosition.existingController
                }
              }
            }

            return (
              <PositionComposer
                collateral={selectedRoute.collateral}
                liability={selectedRoute.liability}
                selectedSubAccountId={selectedPositionId === 'new' ? newPositionSubAccountId ?? undefined : selectedPositionId}
                existingCollateralUsd={existingCollateralUsd}
                existingDebtUsd={existingDebtUsd}
                collateralAmount={collateralAmount}
                onCollateralAmountChange={setCollateralAmount}
                borrowAmount={borrowAmount}
                onBorrowAmountChange={setBorrowAmount}
                existingController={existingController}
                useNativeCollateral={collateralIsWhype ? useNativeCollateral : false}
                receiveNativeBorrow={borrowIsWhype ? receiveNativeBorrow : false}
                activeTab={activeStrategy}
                onActiveTabChange={setActiveStrategy}
                showTabsWhenControlled={true}
                routeType="euler"
              />
            )
          })()
        ) : (
          <div className="flex items-center justify-center rounded-lg border border-border border-dashed bg-panel p-8 text-center">
            <Typography variant="p" className="text-muted-foreground">
              Select collateral and borrow assets to continue.
            </Typography>
          </div>
        )}
      </div>
    </div>
  )
}
